import React from 'react';
import { motion } from 'framer-motion';

/**
 * FloatingOrbs — Soft gradient orbs that float in the background of a section.
 *
 * @param {Array} orbs - Array of orb configs:
 *   { size, color, top, left, delay, duration, animation }
 * @param {string} className - Extra classes on the wrapper
 */
export const FloatingOrbs = ({ orbs = [], className = '' }) => (
  <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden>
    {orbs.map((orb, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full blur-3xl"
        style={{
          width: orb.size || 300,
          height: orb.size || 300,
          top: orb.top || '10%',
          left: orb.left || '10%',
          background: orb.color || 'rgba(59,130,246,0.08)',
        }}
        animate={orb.animate || {
          y: [0, -(orb.drift || 30) * 1.6, (orb.drift || 30) * 0.3, -(orb.drift || 30) * 1.2, 0],
          x: [0, (orb.driftX || 12), -(orb.driftX || 8), (orb.driftX || 6), 0],
          scale: [1, orb.scaleTo || 1.12, 1.02, orb.scaleTo || 1.08, 1],
        }}
        transition={{
          duration: orb.duration || 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: orb.delay || 0,
        }}
      />
    ))}
  </div>
);

/**
 * FloatingShapes — Small geometric shapes (circles, diamonds, etc.) drifting.
 *
 * @param {Array} shapes - Array of shape configs:
 *   { size, color, top, left, delay, type }
 *   type: 'circle' | 'diamond' | 'ring' | 'dot' | 'plus'
 */
export const FloatingShapes = ({ shapes = [], className = '' }) => (
  <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden>
    {shapes.map((shape, i) => {
      const s = shape.size || 8;
      const shapeStyle = {
        position: 'absolute',
        top: shape.top || '20%',
        left: shape.left || '20%',
        width: s,
        height: s,
        opacity: shape.opacity || 0.2,
      };

      let shapeElement;
      switch (shape.type) {
        case 'diamond':
          shapeElement = (
            <div
              style={{ ...shapeStyle, transform: 'rotate(45deg)', background: shape.color || '#3b82f6' }}
              className="rounded-sm"
            />
          );
          break;
        case 'ring':
          shapeElement = (
            <div
              style={{
                ...shapeStyle,
                border: `2px solid ${shape.color || '#3b82f6'}`,
                borderRadius: '50%',
                background: 'transparent',
              }}
            />
          );
          break;
        case 'plus':
          shapeElement = (
            <div style={shapeStyle} className="flex items-center justify-center">
              <svg width={s} height={s} viewBox="0 0 12 12" fill="none">
                <path d="M6 1v10M1 6h10" stroke={shape.color || '#3b82f6'} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </div>
          );
          break;
        case 'dot':
          shapeElement = (
            <div
              style={{ ...shapeStyle, borderRadius: '50%', background: shape.color || '#3b82f6' }}
            />
          );
          break;
        default: // circle
          shapeElement = (
            <div
              style={{ ...shapeStyle, borderRadius: '50%', background: shape.color || '#3b82f6' }}
            />
          );
      }

      return (
        <motion.div
          key={i}
          className="absolute"
          style={{ top: shape.top, left: shape.left }}
          animate={{
            y: [0, -(shape.drift || 20) * 1.5, (shape.drift || 20) * 0.4, -(shape.drift || 20) * 1.2, 0],
            x: [0, (shape.driftX || 12), -(shape.driftX || 8), (shape.driftX || 10), 0],
            rotate: shape.type === 'diamond' ? [45, 120, 60, 90, 45] : [0, (shape.rotate || 8), -(shape.rotate || 5), (shape.rotate || 3), 0],
            opacity: [shape.opacity || 0.2, (shape.opacity || 0.2) * 2, (shape.opacity || 0.2) * 1.2, (shape.opacity || 0.2) * 1.8, shape.opacity || 0.2],
          }}
          transition={{
            duration: shape.duration || 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: shape.delay || 0,
          }}
        >
          {shapeElement}
        </motion.div>
      );
    })}
  </div>
);

/**
 * FloatingIcons — Floating icon wrapper that adds a gentle bob to any child.
 */
export const FloatingIcon = ({ children, delay = 0, duration = 3, y = 6, className = '' }) => (
  <motion.div
    className={className}
    animate={{ y: [0, -y, 0] }}
    transition={{ duration, repeat: Infinity, ease: 'easeInOut', delay }}
  >
    {children}
  </motion.div>
);

/**
 * FloatingParticles — Tiny glowing dots scattered and floating.
 *
 * @param {number} count - How many particles
 * @param {string} color - Base color
 * @param {'light' | 'dark'} theme - Adjusts opacity for bg
 */
export const FloatingParticles = ({ count = 12, color = '#3b82f6', theme = 'light', className = '' }) => {
  const particles = React.useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      size: 3 + Math.random() * 5,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      duration: 4 + Math.random() * 5,
      delay: Math.random() * 4,
      drift: 20 + Math.random() * 40,
      driftX: (Math.random() - 0.5) * 35,
      opacity: theme === 'dark' ? 0.2 + Math.random() * 0.25 : 0.15 + Math.random() * 0.2,
    }));
  }, [count, theme]);

  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden>
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            top: p.top,
            left: p.left,
            background: color,
            opacity: p.opacity,
          }}
          animate={{
            y: [0, -p.drift, p.drift * 0.3, -p.drift * 0.8, 0],
            x: [0, p.driftX, -p.driftX * 0.5, p.driftX * 0.7, 0],
            opacity: [p.opacity, p.opacity * 2.5, p.opacity * 1.2, p.opacity * 2, p.opacity],
            scale: [1, 1.3, 0.9, 1.2, 1],
          }}
          transition={{
            duration: p.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: p.delay,
          }}
        />
      ))}
    </div>
  );
};

export default { FloatingOrbs, FloatingShapes, FloatingIcon, FloatingParticles };
